import { gql } from '@apollo/client';
import { BlockCommodityType, CommodityName } from '@bechtel/sr-types';
import { toLower } from 'lodash';

export const customMetaData = {
  id: '02E94BF5-B7C8-5224-8D74-5D918B93A727',
  name: '',
  memberName: '',
  description: '',
  alternateDescription: '',
  ordinalPosition: 1,
  relatedMemberName: null,
  relatedMemberDisplayField: null,
  columnHeading: '',
  alternateColumnHeading: '',
  columnWidth: 20,
  maxColumnWidth: 80,
  decimals: 0,
  dataType: 'C',
  requiredData: 1,
  columnPrompt: '',
  alternateColumnPrompt: '',
  isUserEditable: false,
  pickListType: null,
  pickListName: null,
  minValue: 0,
  maxValue: 0,
  defaultValue: null,
  changeControl: 0,
  displayColumn: null,
  updatedBy: '',
  updatedTime: '',
  isQueryable: false,
  metadataTableId: '',
  projectId: '',
};

// Commodities which are supported
export const commodities = [
  CommodityName.CABLE,
  CommodityName.EQUIPMENT,
  CommodityName.JUMPER,
  CommodityName.RACEWAY,
  CommodityName.PACKAGE,
  CommodityName.REEL,
  CommodityName.CABLE_CODE,
  CommodityName.EQUIPMENT_CODE,
  CommodityName.RACEWAY_CODE,
  CommodityName.COLOR_CODE,
  CommodityName.EQUIPMENT_BLOCK,
  CommodityName.EQUIPMENT_CODE_BLOCK,
];

// Pick lists for various commodities
export const getPicklistName = (commodity: CommodityName) => {
  const picklistMap = {
    [CommodityName.CABLE]: 'CABLE.TAG',
    [CommodityName.EQUIPMENT]: 'EQUIPMENT.TAG',
    [CommodityName.JUMPER]: 'JUMPER.TAG',
    [CommodityName.RACEWAY]: 'RACEWAY.TAG',
    [CommodityName.PACKAGE]: 'PACKAGE.TAG',
    [CommodityName.REEL]: 'REEL.TAG',
    [CommodityName.CABLE_CODE]: 'CABLE_CODE.TAG',
    [CommodityName.EQUIPMENT_CODE]: 'EQUIPMENT_CODE.TAG',
    [CommodityName.RACEWAY_CODE]: 'RACEWAY_CODE.TAG',
    [CommodityName.COLOR_CODE]: 'COLOR_CODE.TAG',
    [CommodityName.EQUIPMENT_BLOCK]: 'EQUIPMENT_BLOCK_POINT.BLOCK_ID',
    [CommodityName.EQUIPMENT_CODE_BLOCK]: 'EQUIPMENT_CODE_BLOCK_POINT.BLOCK_ID',
  };
  return picklistMap[commodity] || null;
};

// Entity name for various commodities
export const getEntityNameForMutation = (commodity: CommodityName) => {
  const entityMap = {
    [CommodityName.CABLE_CODE]: 'CableCode',
    [CommodityName.EQUIPMENT_CODE]: 'EquipmentCode',
    [CommodityName.RACEWAY_CODE]: 'RacewayCode',
    [CommodityName.COLOR_CODE]: 'ColorCode',
    [CommodityName.EQUIPMENT_BLOCK]: 'Block',
    [CommodityName.EQUIPMENT_CODE_BLOCK]: 'Block',
  };
  return entityMap[commodity] || commodity;
};

// Entity name for rename mutation for various commodities
export const getEntityNameForMutationResponse = (commodity: CommodityName) => {
  const responseMap = {
    [CommodityName.CABLE_CODE]: 'cableCode',
    [CommodityName.EQUIPMENT_CODE]: 'equipmentCode',
    [CommodityName.RACEWAY_CODE]: 'racewayCode',
    [CommodityName.COLOR_CODE]: 'colorCode',
    [CommodityName.EQUIPMENT_BLOCK]: 'block',
    [CommodityName.EQUIPMENT_CODE_BLOCK]: 'block',
  };
  return responseMap[commodity] || toLower(commodity);
};

// Custom query for rename mutation
export const buildGqlMutation = (entityName, entityNameForResponse) => {
  const rawMutation = `mutation Rename${entityName}($rename${entityName}Input: Rename${entityName}Input!) {
    rename${entityName}(rename${entityName}Input: $rename${entityName}Input) {
      errors
      ${entityNameForResponse} {
        tag
      }
    }
  }`;
  return gql`
    ${rawMutation}
  `;
};

// Helper function to sanitize input for rename mutation
export const getSanitizedValues = (values) => {
  const { commodityType, isRevCardAuthorized, parentTag, ...rest } = values;
  if ([CommodityName.PACKAGE, CommodityName.COLOR_CODE].includes(commodityType as CommodityName)) {
    return rest;
  }
  if ([CommodityName.EQUIPMENT_BLOCK, CommodityName.EQUIPMENT_CODE_BLOCK].includes(commodityType as CommodityName)) {
    const blockCommodityType =
      commodityType === CommodityName.EQUIPMENT_BLOCK ? BlockCommodityType.Equipment : BlockCommodityType.EquipmentCode;
    return { ...values, commodityType: blockCommodityType, parentTag };
  }
  return { ...rest, isRevCardAuthorized };
};
